import {ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild, NgZone} from '@angular/core';
import Cropper from "cropperjs";

import { TranslateService } from '@ngx-translate/core';
import { DataURIToBlob } from './../../utils.js'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { environment } from './../../../environments/environment';
import { AlertController, IonModal, LoadingController, Platform } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import heic2any from 'heic2any';

type ViewMode = 0 | 1 | 2 | 3;

@Component({
  selector: 'ngx-photo-editor',
  templateUrl: './ngx-photo-editor.component.html',
  styleUrls: ['./ngx-photo-editor.component.scss'],
  imports: [CommonModule, IonicModule, TranslateModule],
  standalone: true,
})
export class NgxPhotoEditorComponent  implements OnDestroy {
  @ViewChild('modal', { static: false }) modal!: IonModal;
  @ViewChild('image') image!: ElementRef;

  isExist = false;
  private cropper!: Cropper;
  public imgUrl!: string | null;
  private FILES_REGEX = /\.(gif|jpe?g|tiff|png|webp|bmp|pdf|jfif|heic|heif)$/i;
  loading: HTMLIonLoadingElement | null = null;


  @Input() automaticalyClose = false;
  @Input() modalTitle = 'Photo Editor';
  @Input() hideModalHeader = false;
  @Input() aspectRatio = NaN;
  @Input() autoCropArea = 1;
  @Input() autoCrop = true;
  @Input() mask = true;
  @Input() guides = true;
  @Input() centerIndicator = true;
  @Input() viewMode: ViewMode = 0;
  @Input() modalMaxWidth = '500px';
  @Input() modalCentered = false;
  @Input() scalable = true;
  @Input() zoomable = true;
  @Input() cropBoxMovable = true;
  @Input() cropBoxResizable = true;
  @Input() darkTheme = true;
  @Input() roundCropper = false;
  @Input() canvasHeight = 400;

  @Input() resizeToWidth: number | any;
  @Input() resizeToHeight: number | any;
  @Input() imageSmoothingEnabled = true;
  @Input() imageSmoothingQuality: ImageSmoothingQuality = 'high';
  @Input() format: string | any;

  @Input() closeBtnText = 'Close';
  @Input() applyBtnText = 'Apply';

  quality = 92;

  @Input() set imageQuality(value: number) {
    if (value > 0 && value <= 100) {
      this.quality = value;
    } else {
      this.errorEvent.error('Invalid image quality');
    }
  }

  @Output() closeEvent = new EventEmitter<any>();
  @Output() imageCroppedEvent = new EventEmitter<NgxCroppedEvent>();
  @Output() errorEvent = new EventEmitter<any>();
  imageLoaded = false;
  isProcessing = false;



  @Input() set imageChangedEvent(data: File | string | any) {
    this.source = data;
    this.changeDetectorRef.detectChanges();
  }

  @Input() set source(data: File | string | any) {
    if(this.image != null)
      this.image.nativeElement.removeAllListeners('ready')

    if(data == null) {
      this.isExist = false
      return
    }
    this.isExist = true;
    this.changeDetectorRef.detectChanges();

    if (data instanceof Event || (data.hasOwnProperty('target') && data.target.hasOwnProperty('files'))) {
      // @ts-ignore
      const file = data.target.files[0];
      if (file && this.FILES_REGEX.test(file.name)) {
        if (!this.format) {
          // @ts-ignore
          this.format = data.target.files[0].type.split('/')[1];
        }
        if(file.name.toLowerCase().endsWith('.pdf')) {
          const reader = new FileReader();
          this.imgUrl = null;
          reader.onload = (ev: any) => {
            this.pdfStringToBase64(ev.target.result)
            this.forceDetectChanges();
          };
          // @ts-ignore
          reader.readAsDataURL(data.target.files[0]);
        } else if(this.format == 'heic') {
          heic2any({
            blob: data.target.files[0],
            toType: 'image/png'
          }).then((convertHeicTopng: any) => {
            var reader = new FileReader();
            reader.readAsDataURL(convertHeicTopng);
            reader.onload = (ev: any) => {
              this.imgUrl = ev.target.result;
              this.forceDetectChanges();
            };
          })
        } else {
          const reader = new FileReader();
          this.imgUrl = null;
          reader.onload = (ev: any) => {
            this.imgUrl = ev.target.result;
            this.forceDetectChanges();
          };
          // @ts-ignore
          reader.readAsDataURL(data.target.files[0]);
        }
        
        this.openModal();

      } else {
        this.errorEvent.emit('Not supported INPUT');
      }
    } else if (data instanceof File) {
      if (this.FILES_REGEX.test(data.name)) {
        if (!this.format) {
          this.format = data.type.split('/')[1];
        }
        const reader = new FileReader();
        reader.onload = (ev: any) => {
          this.imgUrl = ev.target.result;
          this.forceDetectChanges();
        };
        reader.readAsDataURL(data);
        
        this.openModal();
      } else {
        this.errorEvent.emit('Not supported INPUT');
      }
    } else if (typeof data === 'string') {
      if ((/^data:image\/([a-zA-Z]*);base64,([^\"]*)$/).test(data)) {
        this.imgUrl = data;
        this.format = ((data.split(',')[0]).split(';')[0]).split(':')[1].split('/')[1];
        this.openModal();
      } else if ((/^data:application\/pdf;base64,([^\"]*)$/).test(data)) {
        this.pdfStringToBase64(data)
        this.openModal();
      } else if (this.isValidImageURL(data)) {
        this.imgUrl = data;
        this.format = data.split(/[#?]/)[0].split('.').pop()?.trim() ?? '';
        this.openModal();
      } else {
        this.errorEvent.emit('Not supported URL');
      }
    } else {
      this.errorEvent.emit('Not supported INPUT');
    }
  }

  constructor(
    private translate: TranslateService,
    public loadingController: LoadingController,
    public alertController: AlertController,
    private http: HttpClient,
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone,
    private platform: Platform) { }

  ngOnInit() {
  }

  forceDetectChanges() {
    setTimeout(() => {
      this.changeDetectorRef.detectChanges();
      this.ngZone.run(() => {
        this.changeDetectorRef.detectChanges();
      });
    }, 10);
  }

  openModal() {
    setTimeout(() => {
      this.ngZone.run(() => {
        this.modal.present();
        this.forceDetectChanges();
      });
    }, 10);
  }

  isValidImageURL(str: string) {
    return str.match(/^http[^\?]*.(jpg|jpeg|gif|png|tiff|bmp)(\?(.*))?(\#(.*))?$/gim);
  }

  rotateRight() {
    this.cropper.rotate(45);
  }

  rotateLeft() {
    this.cropper.rotate(-45);
  }

  crop() {
    this.cropper.setDragMode('crop');
  }

  move() {
    this.cropper.setDragMode('move');
  }

  zoomIn() {
    this.cropper.zoom(0.1);
  }

  zoomOut() {
    this.cropper.zoom(-0.1);
  }

  flipH() {
    this.cropper.scaleY(-this.cropper.getImageData().scaleY);
    this.cropper.scaleY(-this.cropper.getImageData().scaleY);
    this.cropper.scaleX(-this.cropper.getImageData().scaleX);
  }

  flipV() {
    this.cropper.scaleX(-this.cropper.getImageData().scaleX);
    this.cropper.scaleX(-this.cropper.getImageData().scaleX);
    this.cropper.scaleY(-this.cropper.getImageData().scaleY);
  }

  reset() {
    this.cropper.reset();
  }

  export() {
    this.isProcessing = true;
    this.imageLoaded = false;

    let width = this.resizeToWidth
    let height = this.resizeToHeight
    if(width == height) {
      let cropedImage = this.cropper.getCroppedCanvas();
      if(cropedImage.width > cropedImage.height) {
        width = null
      } else {
        height = null
      }
    }

    let cropedImage = this.cropper.getCroppedCanvas({
      width: width,
      height: height,
      imageSmoothingEnabled: this.imageSmoothingEnabled,
      imageSmoothingQuality: this.imageSmoothingQuality
    });

    const outputImage = cropedImage.toDataURL('image/' + this.format, this.quality);
    var blob = DataURIToBlob(outputImage);

    this.imageCroppedEvent.emit({
      base64: outputImage,
      file: new File([blob],
        Date.now() + '.' + this.format,
        {type: 'image/' + this.format})
    } as NgxCroppedEvent);

    this.isProcessing = false;
    this.imageLoaded = true;
    this.close();
  }


  close() {
    this.modal.dismiss();
    this.closeEvent.emit();
    this.isExist = false
  }

  ionModalWillDismiss() {
    this.close();
  }

  onModalDidPresent() {
    // Forcer la détection de changement après que le modal soit complètement présenté
    // Ceci est particulièrement important pour iOS
    if (this.platform.is('ios')) {
      setTimeout(() => {
        this.ngZone.run(() => {
          this.changeDetectorRef.detectChanges();
        });
      }, 50);
    }
  }

  onLoad() {
    if(this.image == null)
      return;
    
    this.closeBtnText = this.translate.instant('Close');
    this.applyBtnText = this.translate.instant('Apply');
    this.modalTitle = this.translate.instant('Photo Editor');

    this.image.nativeElement.addEventListener('ready', () => {
      this.imageReady()
    });
    
    if(this.cropper != null)
      this.cropper.destroy();
    this.cropper = new Cropper(this.image.nativeElement, {
      aspectRatio: this.aspectRatio,
      autoCropArea: this.autoCropArea,
      autoCrop: this.autoCrop,
      modal: this.mask, // black mask
      guides: this.guides, // grid
      center: this.centerIndicator, // center indicator
      viewMode: this.viewMode,
      scalable: this.scalable,
      zoomable: this.zoomable,
      background: false,
      cropBoxMovable: this.cropBoxMovable,
      cropBoxResizable: this.cropBoxResizable,
    });
  }

  imageReady() {
    this.imageLoaded = true;
    if(this.automaticalyClose) {
      this.export();
      this.imgUrl = null
    }
  }

  ngOnDestroy(): void {
    //console.log('destroyed');
  }

  error() {
    this.errorEvent.emit('Error loading image');
  }

  async pdfToBase64(file: string) {
    var headers = new HttpHeaders({
      'content-type': 'application/json'
    });
    var formData = {
      'type': 'url',
      'url': file,
    }
    await this.presentLoading();
    this.http.post(environment.API_URL + '../ws_public/getImageBase64FromPDF/', JSON.stringify(formData), {headers : headers}).subscribe(async (data: { [key: string]: any }) => {
        this.imgUrl = data['base64']
        this.loading?.dismiss()
    }, async error => {
      this.loading?.dismiss()
      var alert = await this.alertController.create({
        header: this.translate.instant("Error"),
        subHeader: this.translate.instant("Invalid file"),
        message: this.translate.instant("The PDF file is invalid or too large.") ,
        buttons: ['OK'],
      });
      await alert.present();
    });
  }

  async pdfStringToBase64(data: string) {
    var headers = new HttpHeaders({
      'content-type': 'application/json'
    });
    var formData = {
      'type': 'base64',
      'base64': data,
    }
    await this.presentLoading();
    this.http.post(environment.API_URL + '../ws_public/getImageBase64FromPDF/', JSON.stringify(formData), {headers : headers}).subscribe(async (data: { [key: string]: any }) => { 
        this.source = data['base64']
        this.loading?.dismiss()
    }, async error => {
      this.loading?.dismiss()
      var alert = await this.alertController.create({
        header: this.translate.instant("Error"),
        subHeader: this.translate.instant("Invalid file"),
        message: this.translate.instant("The PDF file is invalid or too large.") ,
        buttons: ['OK'],
      });
      await alert.present();
    });
  }

  async presentLoading() {
    if(this.loading)
      this.stopLoading();

    this.loading = await this.loadingController.create({
      cssClass: 'my-custom-class',
      message: this.translate.instant('Please wait...'),
      duration: 600000
    });
    await this.loading?.present();
  }

  stopLoading() {
    this.loading?.dismiss();
    this.loading = null;
  }
}

export interface NgxCroppedEvent  extends Event {
  base64?: string;
  file?: File;

}


export type imageFormat = 'gif' | 'jpeg' | 'tiff' | 'png' | 'webp' | 'bmp';
